import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { Star, Repeat } from "lucide-react";
import { useLang } from "@/LangContext";
import { Reveal } from "@/components/Reveal";
import { ALLIES } from "@/data/allies";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DonorWall() {
  const { t } = useLang();
  const d = t.donate;
  const { data } = useQuery({
    queryKey: ["donor-wall"],
    queryFn: async () => (await axios.get(`${API}/donations/wall`)).data,
  });
  const donors = data?.donors ?? [];
  const anon = data?.anonymous_count ?? 0;

  return (
    <Reveal delay={0.1}>
      <div
        data-testid="donor-wall"
        className="mt-10 border border-white/10 bg-[#0C0F16] p-7 sm:p-9"
      >
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="font-code text-[11px] uppercase tracking-[0.3em] text-amber-400">
              {d.wallLabel}
            </span>
            <h3 className="mt-2 font-display text-2xl font-bold uppercase text-white sm:text-3xl">
              {d.wallTitle}
            </h3>
          </div>
          {anon > 0 && (
            <span
              data-testid="donor-wall-anon-count"
              className="font-code text-xs uppercase tracking-[0.2em] text-slate-500"
            >
              + {anon} {d.wallAnon}
            </span>
          )}
        </div>

        <div data-testid="allies-logo-grid" className="mt-8">
          <span className="font-code text-[11px] uppercase tracking-[0.3em] text-slate-400">{d.wallCorporate}</span>
          <ul className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {ALLIES.map((x) => (
              <li
                key={x.name}
                data-testid={`ally-logo-${x.name.toLowerCase().replace(/\s+/g, "-")}`}
                className="group relative overflow-hidden border border-white/10 bg-white transition-all duration-500 hover:-translate-y-1 hover:border-amber-400/70"
              >
                <img src={x.logo} alt={x.name} className="aspect-[12/7] w-full object-cover" loading="lazy" />
                <div className="flex items-center justify-between border-t border-black/5 bg-[#0C0F16] px-3 py-2">
                  <span className="font-display text-xs font-bold uppercase tracking-wide text-white">{x.name}</span>
                  <Star size={11} className="text-amber-400" />
                </div>
              </li>
            ))}
          </ul>
        </div>

        {donors.length === 0 ? (
          <p
            data-testid="donor-wall-empty"
            className="mt-6 border border-dashed border-white/15 px-6 py-6 text-center font-display text-base uppercase tracking-wide text-slate-500"
          >
            {d.wallEmpty}
          </p>
        ) : (
          <>
            <span className="mt-8 block font-code text-[11px] uppercase tracking-[0.3em] text-slate-400">{d.wallIndividual}</span>
            <ul className="mt-4 flex flex-wrap gap-3">
            {donors.map((x, i) => (
              <li
                key={x.name}
                data-testid={`donor-wall-item-${i}`}
                className="flex items-center gap-2.5 border border-white/15 bg-[#121620] px-4 py-2.5 transition-colors hover:border-amber-400/50"
              >
                {x.interval === "month" ? (
                  <Repeat size={13} className="text-amber-400" />
                ) : (
                  <Star size={13} className="text-amber-400" />
                )}
                <span className="font-display text-sm font-bold uppercase tracking-wide text-white">
                  {x.name}
                </span>
                <span className="font-code text-[10px] uppercase tracking-[0.2em] text-slate-500">
                  {x.interval === "month" ? d.wallMonthly : d.wallOnce}
                </span>
              </li>
            ))}
          </ul>
          </>
        )}
      </div>
    </Reveal>
  );
}
